import type { SectionItem } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { getId } from '@store/utils'
import { call, select } from 'redux-saga/effects'

import { BASE_COLOR_CLOUDY } from '@/constants/constants'
import type { XmlImportModel } from '@/utils/xml'
import { MAIN_SECTION_TITLE } from '@/utils/xml'
import { addSection, createSectionItem } from '../helpers/addSection'
import { resolveSectionIds } from '../helpers/resolveSectionIds'
import { buildProjectColorMap } from '../utils'
import { applyColors } from './applyColors'
import { addNodeRecursive } from './applyProjectWorkers'
import { createTagMap } from './createTagMap'

export function* applyProject({
  model,
}: {
  model: XmlImportModel
}): Generator<any, void, any> {
  const state: AppState = yield select((currentState) => currentState)
  const activeTab =
    migrationProjectSelector.getActiveOrFirstTabId(state) ||
    state.manager.project.tabs[0]?.id ||
    ''

  if (!activeTab) {
    return
  }

  const existingSections = state.manager.project.sections
    .filter((section) => section.tabId === activeTab)
    .sort((a, b) => a.order - b.order)
  const defaultColorId =
    migrationProjectSelector.getProjectDefaultColorId(state) ||
    state.manager.project.colors[0]?.id ||
    BASE_COLOR_CLOUDY.id
  const projectColorByHex = buildProjectColorMap(state)
  const colorIdMap = new Map<string, string>()
  const tagIdMap = new Map<string, string>()

  yield call(applyColors, {
    model,
    projectColors: state.manager.project.colors,
    projectColorByHex,
    colorIdMap,
  })

  yield call(createTagMap, {
    model,
    defaultColorId,
    state,
    tagIdMap,
    projectColorByHex,
  })

  const sections = resolveSectionIds(model.sections, tagIdMap, colorIdMap)

  let mainSectionId = existingSections[0]?.id
  let newSectionOrder = existingSections.length

  if (!mainSectionId) {
    mainSectionId = getId()
    const mainSection = createSectionItem({
      id: mainSectionId,
      title: MAIN_SECTION_TITLE,
      tabId: activeTab,
      order: 0,
    })

    yield call(addSection, {
      section: mainSection,
      index: 0,
      tabId: activeTab,
    })
    newSectionOrder = Math.max(newSectionOrder, 1)
  }

  if (!mainSectionId) {
    return
  }

  for (const [sectionIndex, sourceSection] of sections.entries()) {
    let targetSectionId: string

    if (sectionIndex === 0) {
      targetSectionId = mainSectionId
    } else {
      const sectionId = sourceSection.id
      const section: SectionItem = createSectionItem({
        id: sectionId,
        title: sourceSection.title,
        tabId: activeTab,
        order: newSectionOrder,
      })

      yield call(addSection, {
        section,
        index: newSectionOrder,
        tabId: activeTab,
      })
      targetSectionId = sectionId
      newSectionOrder += 1
    }

    for (const [nodeIndex, sourceNode] of sourceSection.nodes.entries()) {
      yield call(addNodeRecursive, {
        node: sourceNode,
        parentId: targetSectionId,
        index: nodeIndex,
      })
    }
  }
}
